import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  useTheme,
  Container,
  TextField,
  InputAdornment,
  IconButton,
  Pagination,
  Avatar,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Skeleton,
  Card,
  CardContent,
  Grid,
  useMediaQuery,
} from "@mui/material";
import { Search, Clear, Person, ArrowUpward, ArrowDownward, UnfoldMore } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { AgentService } from "../../services/agent.service";
import { showSnackbar } from "../../store/system.store";
import { StoreState } from "../../store/store";

import useScreen from "../../components/common/UseScreen";
import { useDebounce } from "../../hooks/use-debounce";
import { AgentClient } from "./interfaces";
import { ReactCountryFlag } from "react-country-flag";

const PAGE_SIZE = 15;

type SortOrder = 'asc' | 'desc';
type SortableColumn = 'Full_Name' | 'Email' | 'Phone' | 'Country' | 'Lead_Source' | 'Created_Time' | 'totalLeftToPay';

// Country name to country code mapping
const getCountryCode = (countryName: string): string => {
  if (!countryName) return 'UN';
  
  // Capitalize the country name to handle case-insensitive matching
  const capitalizedCountryName = countryName
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  
  const countryMap: Record<string, string> = {
    'United States': 'US',
    'Usa': 'US',
    'United Kingdom': 'GB',
    'Uk': 'GB',
    'Canada': 'CA',
    'Australia': 'AU',
    'Germany': 'DE',
    'France': 'FR',
    'Italy': 'IT',
    'Spain': 'ES',
    'Netherlands': 'NL',
    'Belgium': 'BE',
    'Switzerland': 'CH',
    'Austria': 'AT',
    'Sweden': 'SE',
    'Norway': 'NO',
    'Denmark': 'DK',
    'Finland': 'FI',
    'Ireland': 'IE',
    'Portugal': 'PT',
    'Poland': 'PL',
    'Czech Republic': 'CZ',
    'Hungary': 'HU',
    'Slovakia': 'SK',
    'Slovenia': 'SI',
    'Croatia': 'HR',
    'Serbia': 'RS',
    'Bulgaria': 'BG',
    'Romania': 'RO',
    'Greece': 'GR',
    'Cyprus': 'CY',
    'Malta': 'MT',
    'Luxembourg': 'LU',
    'Iceland': 'IS',
    'Estonia': 'EE',
    'Latvia': 'LV',
    'Lithuania': 'LT',
    'Japan': 'JP',
    'South Korea': 'KR',
    'China': 'CN',
    'India': 'IN',
    'Israel': 'IL',
    'Turkey': 'TR',
    'Russia': 'RU',
    'Ukraine': 'UA',
    'Brazil': 'BR',
    'Argentina': 'AR',
    'Mexico': 'MX',
    'Chile': 'CL',
    'Colombia': 'CO',
    'Peru': 'PE',
    'Venezuela': 'VE',
    'South Africa': 'ZA',
    'Egypt': 'EG',
    'Morocco': 'MA',
    'Nigeria': 'NG',
    'Kenya': 'KE',
    'Ghana': 'GH',
    'New Zealand': 'NZ',
    'Singapore': 'SG',
    'Malaysia': 'MY',
    'Thailand': 'TH',
    'Philippines': 'PH',
    'Indonesia': 'ID',
    'Vietnam': 'VN',
  };
  
  return countryMap[capitalizedCountryName] || 'UN'; // Default to UN flag if country not found
};

const AdminClientListPage: React.FC = () => {
  const [clients, setClients] = useState<AgentClient[] | null>(null);
  const [sortedClients, setSortedClients] = useState<AgentClient[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<SortableColumn | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const user = useSelector((state: StoreState) => state.user.loggedInUser);
  const isAdmin = user?.role === 'admin';

  const { isDesktop } = useScreen();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const debouncedQuery = useDebounce({ value: searchQuery, delay: 700 });

  const handleSort = (column: SortableColumn) => {
    const isAsc = sortColumn === column && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const sortData = (data: AgentClient[], column: SortableColumn | null, order: SortOrder): AgentClient[] => {
    if (!column || !data) return data;

    return [...data].sort((a, b) => {
      let aValue: any = a[column];
      let bValue: any = b[column];

      // Handle date sorting
      if (column === 'Created_Time') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      // Handle totalLeftToPay sorting (treat undefined as 0)
      if (column === 'totalLeftToPay') {
        aValue = aValue || 0;
        bValue = bValue || 0;
      }

      // Handle string sorting
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return order === 'asc' ? comparison : -comparison;
      }

      // Handle numeric sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  };

  const getSortIcon = (column: SortableColumn) => {
    if (sortColumn !== column) {
      return <UnfoldMore sx={{ fontSize: '16px', color: 'text.secondary' }} />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUpward sx={{ fontSize: '16px', color: 'primary.main' }} />
      : <ArrowDownward sx={{ fontSize: '16px', color: 'primary.main' }} />;
  };

  const fetchClients = async (page: number, query: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await AgentService.getAgentClients({
        page,
        perPage: PAGE_SIZE,
        query,
      });

      const totalPagesData = Math.ceil((response.info?.count || 0) / (response.info?.per_page || PAGE_SIZE));

      setClients(response.data);
      setSortedClients(response.data);
      setTotalCount(response.info.count);
      setTotalPages(totalPagesData);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setError("Failed to load clients. Please try again.");
      setClients(null);
      setSortedClients(null);
      setTotalCount(0);
      setTotalPages(1);

      dispatch(
        showSnackbar({
          isError: true,
          msg: "Failed to load clients. Please try again.",
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
    setSortColumn(null); // Reset sorting when search changes
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
    setSortColumn(null); // Reset sorting when clearing search
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleClientClick = (clientId: string) => {
    navigate(`/agent/client/${clientId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getInitials = (fullName: string) => {
    return fullName
      .split(" ")
      .map((name) => name.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getLeadSourceColor = (source: string): "primary" | "success" | "warning" | "info" | "secondary" | "default" => {
    const colors: Record<string, "primary" | "success" | "warning" | "info" | "secondary"> = {
      "PPC Campaign": "primary",
      Website: "success",
      Referral: "warning",
      "Social Media": "info",
      Email: "secondary",
    };
    return colors[source] || "default";
  };

  const renderSkeletonRows = () => {
    return Array.from({ length: PAGE_SIZE }).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton variant="text" width="60%" height={20} />
          </Box>
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width="80%" height={20} />
        </TableCell>
        {isAdmin && (
          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
            <Skeleton variant="text" width="70%" height={20} />
          </TableCell>
        )}
        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          <Skeleton variant="text" width="50%" height={20} />
        </TableCell>
        {isAdmin && (
          <TableCell>
            <Skeleton variant="rounded" width={80} height={24} />
          </TableCell>
        )}
        {isAdmin && (
          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
            <Skeleton variant="text" width="60%" height={20} />
          </TableCell>
        )}
        {isAdmin && (
          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
            <Skeleton variant="text" width="40%" height={16} />
          </TableCell>
        )}
        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          <Skeleton variant="text" width="60%" height={20} />
        </TableCell>
      </TableRow>
    ));
  };

  const renderMobileSkeletonCards = () => {
    return Array.from({ length: PAGE_SIZE }).map((_, index) => (
      <Card 
        key={`mobile-skeleton-${index}`}
        sx={{ 
          mb: 2, 
          borderRadius: 3,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Skeleton variant="circular" width={50} height={50} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="70%" height={24} />
              <Skeleton variant="text" width="90%" height={20} />
            </Box>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Skeleton variant="text" width="60%" height={16} />
              <Skeleton variant="text" width="80%" height={20} />
            </Grid>
            <Grid item xs={6}>
              <Skeleton variant="text" width="50%" height={16} />
              <Skeleton variant="rounded" width={70} height={24} />
            </Grid>
            <Grid item xs={6}>
              <Skeleton variant="text" width="40%" height={16} />
              <Skeleton variant="text" width="60%" height={20} />
            </Grid>
            <Grid item xs={6}>
              <Skeleton variant="text" width="45%" height={16} />
              <Skeleton variant="text" width="55%" height={20} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    ));
  };

  const MobileClientCard = ({ client }: { client: AgentClient }) => (
    <Card 
      onClick={() => handleClientClick(client.id)}
      sx={{ 
        mb: 2, 
        cursor: 'pointer',
        borderRadius: 3,
        transition: 'all 0.2s ease-in-out',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Avatar
            sx={{
              width: 50,
              height: 50,
              bgcolor: theme.palette.primary.main,
              fontSize: "1.1rem",
              fontWeight: "bold",
            }}
          >
            {getInitials(client.Full_Name)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                fontSize: '1.1rem',
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {client.Full_Name}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {client.Email}
            </Typography>
          </Box>
        </Box>
        
        <Grid container spacing={2}>
          {isAdmin && (
            <Grid item xs={6}>
              <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>
                Phone
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {client.Phone || 'N/A'}
              </Typography>
            </Grid>
          )}
          <Grid item xs={6}>
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>
              Country
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <ReactCountryFlag
                countryCode={getCountryCode(client.Country)}
                svg
                style={{
                  width: '16px',
                  height: '12px',
                }}
              />
              <Typography variant="body2">
                {client.Country ? client.Country.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'N/A'}
              </Typography>
            </Box>
          </Grid>
          {isAdmin && (
            <Grid item xs={6}>
              <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>
                Lead Source
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip 
                  label={client.Lead_Source} 
                  size="small" 
                  color={getLeadSourceColor(client.Lead_Source)} 
                />
              </Box>
            </Grid>
          )}
          {isAdmin && (
            <Grid item xs={6}>
              <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>
                Created
              </Typography>
              <Typography variant="body2">
                {formatDate(client.Created_Time)}
              </Typography>
            </Grid>
          )}
          <Grid item xs={6}>
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>
              Total Due
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: (client.totalLeftToPay !== undefined && client.totalLeftToPay > 0) ? 600 : 400,
                color: (client.totalLeftToPay !== undefined && client.totalLeftToPay > 0) ? 'error.main' : 'text.secondary',
                mt: 0.5
              }}
            >
              {client.totalLeftToPay !== undefined ? `$${client.totalLeftToPay.toLocaleString()}` : '-'}
            </Typography>
          </Grid>
        </Grid>
        
        {isAdmin && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.disabled">
              ID: {client.id}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  useEffect(() => {
    fetchClients(currentPage, debouncedQuery);
  }, [currentPage, debouncedQuery]);

  useEffect(() => {
    if (clients) {
      const sorted = sortData(clients, sortColumn, sortOrder);
      setSortedClients(sorted);
    }
  }, [clients, sortColumn, sortOrder]);

  if (error)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Person sx={{ fontSize: 64, color: theme.palette.grey[400], mb: 2 }} />
        <Typography variant="h5" color="text.secondary" gutterBottom>
          {debouncedQuery ? "No matching clients found" : "No clients found"}
        </Typography>
      </Box>
    );


  return (
    <Box
      sx={{
        padding: isDesktop ? "24px" : "16px",
      }}
    >

      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Stack
            direction={isDesktop ? "row" : "column"}
            justifyContent="space-between"
            alignItems={isDesktop ? "center" : "stretch"}
            spacing={2}
            mb={3}
            sx={{ display: "flex", width: "100%", justifyContent: "center" }}
          >
            <TextField
              fullWidth
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch} size="small">
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: 600,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: "12px",
                  "& fieldset": {
                    borderColor: theme.palette.divider,
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />
          </Stack>
        </Box>

        {isMobile ? (
          // Mobile Card Layout
          <Box>
            {isLoading ? (
              renderMobileSkeletonCards()
            ) : !sortedClients?.length ? (
              <Paper
                sx={{
                  p: 4,
                  textAlign: "center",
                  backgroundColor: theme.palette.grey[50],
                  borderRadius: "16px",
                }}
              >
                <Person sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  {searchQuery ? `No clients match "${searchQuery}". Try a different search term.` : "You don't have any clients assigned to you yet."}
                </Typography>
              </Paper>
            ) : (
              sortedClients?.map((client) => (
                <MobileClientCard key={client.id} client={client} />
              ))
            )}
          </Box>
        ) : !isLoading && !sortedClients?.length ? (
          <Paper
            sx={{
              p: 6,
              textAlign: "center",
              backgroundColor: theme.palette.grey[50],
              borderRadius: "16px",
            }}
          >
            <Person sx={{ fontSize: 64, color: theme.palette.grey[400], mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {searchQuery ? `No clients match "${searchQuery}". Try a different search term.` : "You don't have any clients assigned to you yet."}
            </Typography>
          </Paper>
        ) : (
          <>
            <TableContainer 
              component={Paper} 
              elevation={3}
              sx={{ 
                borderRadius: "16px", 
                overflow: "auto", 
                mb: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                maxWidth: '100%'
              }}
            >
              <Table stickyHeader sx={{ minWidth: { xs: 600, sm: 700, md: 800 } }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                    <TableCell 
                      onClick={!isLoading ? () => handleSort('Full_Name') : undefined}
                      sx={{ 
                        fontWeight: "bold", 
                        cursor: isLoading ? "default" : "pointer",
                        userSelect: 'none',
                        '&:hover': !isLoading ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {},
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Name
                        {!isLoading && getSortIcon('Full_Name')}
                      </Box>
                    </TableCell>
                    <TableCell 
                      onClick={!isLoading ? () => handleSort('Email') : undefined}
                      sx={{ 
                        fontWeight: "bold", 
                        cursor: isLoading ? "default" : "pointer",
                        userSelect: 'none',
                        '&:hover': !isLoading ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {}
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Email
                        {!isLoading && getSortIcon('Email')}
                      </Box>
                    </TableCell>
                    {isAdmin && (
                      <TableCell 
                        onClick={!isLoading ? () => handleSort('Phone') : undefined}
                        sx={{ 
                          fontWeight: "bold", 
                          cursor: isLoading ? "default" : "pointer",
                          userSelect: 'none',
                          '&:hover': !isLoading ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {},
                          display: { xs: 'none', sm: 'table-cell' }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Phone
                          {!isLoading && getSortIcon('Phone')}
                        </Box>
                      </TableCell>
                    )}
                    <TableCell 
                      onClick={!isLoading ? () => handleSort('Country') : undefined}
                      sx={{ 
                        fontWeight: "bold", 
                        cursor: isLoading ? "default" : "pointer",
                        userSelect: 'none',
                        '&:hover': !isLoading ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {},
                        display: { xs: 'none', md: 'table-cell' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Country
                        {!isLoading && getSortIcon('Country')}
                      </Box>
                    </TableCell>
                    {isAdmin && (
                      <TableCell 
                        onClick={!isLoading ? () => handleSort('Lead_Source') : undefined}
                        sx={{ 
                          fontWeight: "bold", 
                          cursor: isLoading ? "default" : "pointer",
                          userSelect: 'none',
                          '&:hover': !isLoading ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {}
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Lead Source
                          {!isLoading && getSortIcon('Lead_Source')}
                        </Box>
                      </TableCell>
                    )}
                    {isAdmin && (
                      <TableCell 
                        onClick={!isLoading ? () => handleSort('Created_Time') : undefined}
                        sx={{ 
                          fontWeight: "bold", 
                          cursor: isLoading ? "default" : "pointer",
                          userSelect: 'none',
                          '&:hover': !isLoading ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {},
                          display: { xs: 'none', lg: 'table-cell' }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Created
                          {!isLoading && getSortIcon('Created_Time')}
                        </Box>
                      </TableCell>
                    )}
                    {isAdmin && (
                      <TableCell sx={{ fontWeight: "bold", display: { xs: 'none', sm: 'table-cell' } }}>ID</TableCell>
                    )}
                    <TableCell 
                      onClick={!isLoading ? () => handleSort('totalLeftToPay') : undefined}
                      sx={{ 
                        fontWeight: "bold", 
                        cursor: isLoading ? "default" : "pointer",
                        userSelect: 'none',
                        '&:hover': !isLoading ? { backgroundColor: 'rgba(0,0,0,0.04)' } : {},
                        display: { xs: 'none', md: 'table-cell' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Total Due
                        {!isLoading && getSortIcon('totalLeftToPay')}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    renderSkeletonRows()
                  ) : (
                    sortedClients?.map((client) => (
                    <TableRow
                      key={client.id}
                      onClick={() => handleClientClick(client.id)}
                      sx={{
                        cursor: "pointer",
                        transition: 'all 0.2s ease-in-out',
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                          transform: 'translateY(-1px)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        },
                        "&:nth-of-type(even)": {
                          backgroundColor: theme.palette.grey[50],
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: theme.palette.primary.main,
                              fontSize: "0.9rem",
                              fontWeight: "bold",
                            }}
                          >
                            {getInitials(client.Full_Name)}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {client.Full_Name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{client.Email}</Typography>
                      </TableCell>
                      {isAdmin && (
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Typography variant="body2">{client.Phone}</Typography>
                        </TableCell>
                      )}
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ReactCountryFlag
                            countryCode={getCountryCode(client.Country)}
                            svg
                            style={{
                              width: '20px',
                              height: '15px',
                            }}
                          />
                          <Typography variant="body2">
                            {client.Country ? client.Country.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      {isAdmin && (
                        <TableCell>
                          <Chip label={client.Lead_Source} size="small" color={getLeadSourceColor(client.Lead_Source)} />
                        </TableCell>
                      )}
                      {isAdmin && (
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(client.Created_Time)}
                          </Typography>
                        </TableCell>
                      )}
                      {isAdmin && (
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Typography variant="caption" color="text.disabled">
                            {client.id}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: (client.totalLeftToPay !== undefined && client.totalLeftToPay > 0) ? 600 : 400,
                            color: (client.totalLeftToPay !== undefined && client.totalLeftToPay > 0) ? 'error.main' : 'text.secondary'
                          }}
                        >
                          {client.totalLeftToPay !== undefined ? `$${client.totalLeftToPay.toLocaleString()}` : '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3, px: 2 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size={isMobile ? "small" : isDesktop ? "large" : "medium"}
                  showFirstButton={!isMobile}
                  showLastButton={!isMobile}
                  siblingCount={isMobile ? 0 : 1}
                  boundaryCount={isMobile ? 1 : 2}
                  sx={{
                    "& .MuiPaginationItem-root": {
                      borderRadius: "12px",
                    },
                  }}
                />
              </Box>
            )}

            {!isLoading && totalCount > 0 && (
              <Box sx={{ textAlign: "center", px: 2 }}>
                <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
                  Showing {Math.min((currentPage - 1) * PAGE_SIZE + 1, totalCount)} to {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}{" "}
                  clients
                  {searchQuery && !isMobile && ` matching "${searchQuery}"`}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default AdminClientListPage;
